import epsClientV1 from 'enrollment-platform/api/eps-v1.client'
import type { AbilityRule } from 'enrollment-platform/rbac/ability'

const accessControlClient = epsClientV1

export interface AuditLogEntry {
  id: string
  timestamp: string
  actorId: string
  actorName: string
  action: string
  description: string
  additionalData?: Record<string, unknown>
}

export interface AuditLogPage {
  data: AuditLogEntry[]
  total: number
  page: number
  limit: number
}

export interface AuditLogQuery {
  search?: string
  dateFrom?: string
  dateTo?: string
  page?: number
  limit?: number
  targetId?: string
}

export interface RoleDto {
  id: string
  name: string
  description?: string
  status: string
  createdAt: string
  userCount: number
}

export interface PermissionDto {
  subjectId: string
  actions: string[]
}

// The subject (menu) catalog from the DB. `actions` excludes the Manage
// wildcard — the UI layers Manage onto every subject itself.
export interface SubjectDto {
  subjectId: string
  actions: string[]
}

export interface RoleDetailDto extends RoleDto {
  permissions: PermissionDto[]
}

// The RBAC role a user is implicitly granted based on their Cognito role,
// surfaced by the API only when they have NO explicitly granted RBAC role. Null
// (or absent) means explicit grants exist, so nothing is implied.
export interface ImpliedRoleDto {
  id: string
  name: string
}

/** A granted role resolved to its catalog name. */
export interface UserRoleDto {
  id: string
  name: string
}

export interface UserDto {
  id: string
  cognitoUserId: string
  personUserId?: string
  email?: string
  // Sign-in (IdP) email. Present when it differs from the profile `email`; some
  // accounts sign in with a legacy/IdP-side address.
  cognitoEmail?: string
  firstName?: string
  lastName?: string
  companyName?: string
  clientPublicKey?: string
  roleIds: string[]
  // The granted roles named, in roleIds order. Sent on the list and per-user
  // reads so a viewer without access to the roles catalog can still name them.
  roles?: UserRoleDto[]
  status: string
  // Cognito role from the profile (persons.user_role).
  cognitoRole?: string
  // Cognito role from the cognito_users mirror; may differ from `cognitoRole`.
  cognitoUserRole?: string
  // No active row in the cognito_users mirror — never synced or archived. The
  // backend sends false while the mirror is empty, so it never fires en masse.
  hasNoCognitoRecord?: boolean
  administeredCount?: number
  administeredCompanies?: string[]
  impliedRole?: ImpliedRoleDto | null
}

/** POST users/lookup: rows for the requested ids that have a person row, in request order. */
export interface LookupUsersResponse {
  data: UserDto[]
}

export interface ListUsersQuery {
  search?: string
  clientPublicKey?: string
  roleIds?: string
  cognitoRole?: string
  status?: string
  syncMatch?: 'diff' | 'same' | 'missing'
  page?: number
  limit?: number
}

export interface UsersPage {
  data: UserDto[]
  total: number
  page: number
  limit: number
}

export interface TriggerCognitoSyncResponse {
  status: 'queued'
  jobId: string
}

export interface CreateRoleInputDto {
  name: string
  description?: string
  status: string
}

const BASE = 'access-control'

export const accessControlApi = {
  async listSubjects(): Promise<SubjectDto[]> {
    return accessControlClient
      .get<SubjectDto[]>(`${BASE}/subjects`)
      .then((r) => r.data)
  },

  async listRoles(): Promise<RoleDto[]> {
    return accessControlClient
      .get<RoleDto[]>(`${BASE}/roles`)
      .then((r) => r.data)
  },

  async createRole(input: CreateRoleInputDto): Promise<RoleDto> {
    return accessControlClient
      .post<RoleDto>(`${BASE}/roles`, input)
      .then((r) => r.data)
  },

  async getRoleDetail(roleId: string): Promise<RoleDetailDto> {
    return accessControlClient
      .get<RoleDetailDto>(`${BASE}/roles/${roleId}`)
      .then((r) => r.data)
  },

  async upsertRolePermissions(
    roleId: string,
    permissions: PermissionDto[]
  ): Promise<void> {
    await accessControlClient.put(`${BASE}/roles/${roleId}/permissions`, {
      permissions,
    })
  },

  async listRoleUsers(roleId: string): Promise<UserDto[]> {
    return accessControlClient
      .get<UserDto[]>(`${BASE}/roles/${roleId}/users`)
      .then((r) => r.data)
  },

  async syncRoleUsers(roleId: string, personIds: string[]): Promise<void> {
    await accessControlClient.put(`${BASE}/roles/${roleId}/users`, {
      personIds,
    })
  },

  async listUsers(params?: ListUsersQuery): Promise<UsersPage> {
    return accessControlClient
      .get<UsersPage>(`${BASE}/users`, { params })
      .then((r) => r.data)
  },

  /** One user's roles. Guarded by Read on UserPermission, not by Permission Management access. */
  async getUser(cognitoUserId: string): Promise<UserDto> {
    return accessControlClient
      .get<UserDto>(`${BASE}/users/${encodeURIComponent(cognitoUserId)}`)
      .then((r) => r.data)
  },

  /**
   * Many users' roles in one request, for pages that list people. The ids are
   * UUIDs and a list can hold many, so they travel in the body. Guarded like
   * getUser; ids with no person row are left out of the response.
   */
  async lookupUsers(cognitoUserIds: string[]): Promise<UserDto[]> {
    return accessControlClient
      .post<LookupUsersResponse>(`${BASE}/users/lookup`, { cognitoUserIds })
      .then((r) => r.data.data)
  },

  async triggerCognitoSync(): Promise<TriggerCognitoSyncResponse> {
    return accessControlClient
      .post<TriggerCognitoSyncResponse>(`${BASE}/users/cognito-sync`)
      .then((r) => r.data)
  },

  async updateUserAccess(
    cognitoUserId: string,
    body: { roleIds: string[] }
  ): Promise<{ id: string }> {
    return accessControlClient
      .put<{ id: string }>(`${BASE}/users/${cognitoUserId}/access`, body)
      .then((r) => r.data)
  },

  async getAuditLog(params: AuditLogQuery): Promise<AuditLogPage> {
    return accessControlClient
      .get<AuditLogPage>(`${BASE}/audit-log`, { params })
      .then((r) => r.data)
  },

  async getMyAbility(): Promise<{ rules: AbilityRule[] }> {
    return accessControlClient
      .get<{ rules: AbilityRule[] }>(`${BASE}/me/ability`)
      .then((r) => r.data)
  },
}
